// apps/mobile/src/app/screens/ChatHistoryScreen.tsx
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, MessageSquare, Trash2, Pencil, Plus, Zap, BookOpen } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useApp } from '../context/AppContext';
import chatThreadService, { type ChatThreadSummary } from '../services/chatThreadService';
import { notify } from '../utils/notifications';

// ── Date grouping ─────────────────────────────────────────────────────────────

function getDateGroup(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart.getTime() - 86400000);
  const weekStart = new Date(todayStart.getTime() - 6 * 86400000);
  if (d >= todayStart) return 'Today';
  if (d >= yesterdayStart) return 'Yesterday';
  if (d >= weekStart) return 'This Week';
  return 'Earlier';
}

const GROUP_ORDER = ['Today', 'Yesterday', 'This Week', 'Earlier'];

// ── Context icon ──────────────────────────────────────────────────────────────

function ContextIcon({ type }: { type: string }) {
  const cls = 'w-4 h-4 flex-shrink-0';
  if (type === 'paper') return <BookOpen className={cls} />;
  if (type === 'session') return <Zap className={cls} />;
  return <MessageSquare className={cls} />;
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function ChatHistoryScreen() {
  const navigate = useNavigate();
  const {
    currentEventId,
    authSession,
    isAuthSessionCurrent,
  } = useApp();
  const [threads, setThreads] = useState<ChatThreadSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    const owner = authSession;
    if (!owner || !isAuthSessionCurrent(owner)) return;
    setThreads([]);
    setLoading(true);
    chatThreadService.list(
      currentEventId ?? undefined,
      { signal: owner.signal },
    )
      .then(data => {
        if (!cancelled && isAuthSessionCurrent(owner)) setThreads(data);
      })
      .catch(() => {
        if (!cancelled && isAuthSessionCurrent(owner)) {
          notify.error('Could not load chat history');
        }
      })
      .finally(() => {
        if (!cancelled && isAuthSessionCurrent(owner)) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [authSession, currentEventId, isAuthSessionCurrent]);

  useEffect(() => {
    if (renamingId) setTimeout(() => renameInputRef.current?.focus(), 50);
  }, [renamingId]);

  const handleOpen = (thread: ChatThreadSummary) => {
    if (renamingId) return;
    navigate('/report-builder', {
      state: {
        thread_id: thread.id,
        thread_title: thread.title,
        thread_summary: thread.summary ?? undefined,
        context: {
          mode: thread.context_type === 'paper' ? 'paper-insight'
               : thread.context_type === 'session' ? 'session-prep'
               : 'general',
          eventId: currentEventId ?? undefined,
          paperId: thread.context_type === 'paper' ? thread.context_id ?? undefined : undefined,
          sessionId: thread.context_type === 'session' ? thread.context_id ?? undefined : undefined,
        },
      },
    });
  };

  const startRename = (thread: ChatThreadSummary) => {
    setRenamingId(thread.id);
    setRenameValue(thread.title);
  };

  const commitRename = async (thread: ChatThreadSummary) => {
    const owner = authSession;
    const newTitle = renameValue.trim();
    if (
      !newTitle ||
      newTitle === thread.title ||
      !owner ||
      !isAuthSessionCurrent(owner)
    ) {
      setRenamingId(null);
      return;
    }
    try {
      const updated = await chatThreadService.rename(
        thread.id,
        newTitle,
        { signal: owner.signal },
      );
      if (!isAuthSessionCurrent(owner)) return;
      setThreads(prev => prev.map(t => t.id === thread.id ? updated : t));
    } catch {
      if (isAuthSessionCurrent(owner)) notify.error('Could not rename thread');
    } finally {
      if (isAuthSessionCurrent(owner)) setRenamingId(null);
    }
  };

  const handleDelete = async (thread: ChatThreadSummary) => {
    const owner = authSession;
    if (!owner || !isAuthSessionCurrent(owner)) return;
    setThreads(prev => prev.filter(t => t.id !== thread.id));
    try {
      await chatThreadService.delete(
        thread.id,
        { signal: owner.signal },
      );
      if (!isAuthSessionCurrent(owner)) return;
    } catch {
      if (!isAuthSessionCurrent(owner)) return;
      notify.error('Could not delete thread');
      if (!isAuthSessionCurrent(owner)) return;
      const data = await chatThreadService.list(
        currentEventId ?? undefined,
        { signal: owner.signal },
      );
      if (!isAuthSessionCurrent(owner)) return;
      setThreads(data);
    }
  };

  const grouped: Record<string, ChatThreadSummary[]> = {};
  for (const t of threads) {
    const g = getDateGroup(t.updated_at);
    if (!grouped[g]) grouped[g] = [];
    grouped[g].push(t);
  }

  return (
    <div className="absolute inset-0 flex flex-col" style={{ backgroundColor: 'var(--app-bg)' }}>
      {/* Header */}
      <div
        className="app-screen-header flex items-center gap-3 px-4 pt-12 pb-4 border-b flex-shrink-0"
        style={{ borderColor: 'var(--app-border)' }}
      >
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-lg" style={{ color: 'var(--app-text-muted)' }}>
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-lg font-semibold flex-1" style={{ color: 'var(--app-text)' }}>
          Chat History
        </h1>
        <button
          onClick={() => navigate('/report-builder', { state: { context: { mode: 'general', eventId: currentEventId ?? undefined } } })}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium"
          style={{ backgroundColor: 'var(--app-accent)', color: '#fff' }}
        >
          <Plus className="w-4 h-4" />
          New
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {loading && (
          <div className="flex justify-center pt-16">
            <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--app-accent)' }} />
          </div>
        )}

        {!loading && threads.length === 0 && (
          <div className="flex flex-col items-center justify-center pt-24 gap-4 text-center">
            <MessageSquare className="w-12 h-12 opacity-20" style={{ color: 'var(--app-text-muted)' }} />
            <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>
              No chat history yet.<br />Start a conversation with the AI Copilot.
            </p>
          </div>
        )}

        {GROUP_ORDER.filter(g => grouped[g]?.length).map(group => (
          <div key={group} className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-wider mb-2 px-1" style={{ color: 'var(--app-text-muted)' }}>
              {group}
            </p>
            <div className="flex flex-col gap-2">
              <AnimatePresence initial={false}>
                {grouped[group].map(thread => (
                  <motion.div
                    key={thread.id}
                    layout
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ duration: 0.18 }}
                  >
                    <div
                      className="rounded-xl border p-3 flex items-start gap-3"
                      style={{ borderColor: 'var(--app-border)', backgroundColor: 'var(--app-surface)' }}
                    >
                      {/* Icon */}
                      <div className="mt-0.5" style={{ color: 'var(--app-accent)' }}>
                        <ContextIcon type={thread.context_type} />
                      </div>

                      {/* Content — tappable */}
                      <div className="flex-1 min-w-0" onClick={() => handleOpen(thread)}>
                        {renamingId === thread.id ? (
                          <input
                            ref={renameInputRef}
                            value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            onBlur={() => commitRename(thread)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') commitRename(thread);
                              if (e.key === 'Escape') setRenamingId(null);
                            }}
                            onClick={e => e.stopPropagation()}
                            className="w-full text-sm font-medium bg-transparent border-b outline-none"
                            style={{ color: 'var(--app-text)', borderColor: 'var(--app-accent)' }}
                          />
                        ) : (
                          <p className="text-sm font-medium truncate" style={{ color: 'var(--app-text)' }}>
                            {thread.context_type === 'general' && thread.context_date
                              ? `General · ${new Date(thread.context_date).toLocaleDateString([], { month: 'short', day: 'numeric' })}`
                              : thread.title}
                          </p>
                        )}
                        {thread.last_message && (
                          <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--app-text-muted)' }}>
                            {thread.last_message}
                          </p>
                        )}
                        <p className="text-xs mt-1" style={{ color: 'var(--app-text-muted)' }}>
                          {new Date(thread.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={e => { e.stopPropagation(); startRename(thread); }}
                          className="p-1.5 rounded-lg"
                          style={{ color: 'var(--app-text-muted)' }}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); handleDelete(thread); }}
                          className="p-1.5 rounded-lg"
                          style={{ color: '#ef4444' }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
